import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * SecureContent Component
 * Wraps content that should be protected from screenshots and screen recording
 * 
 * Features:
 * - Prevents screenshots (content appears black/blurred in captures)
 * - Adds watermarking for audit trails
 * - Prevents text selection and copying
 * - Disables right-click context menu
 * 
 * @example
 * <SecureContent level="high">
 *   <PatientHealthInfo />
 * </SecureContent>
 */
const SecureContent = ({ 
  children, 
  level = 'medium',
  className = '',
  watermark = true,
  allowSelection = false,
  style = {}
}) => {
  const contentRef = useRef(null);

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    // Apply security attributes
    element.setAttribute('data-secure-level', level);
    
    // Add secure flag for native apps (if running in WebView)
    if (window.ReactNativeWebView) {
      element.setAttribute('data-secure-flag', 'true');
    }

    // Prevent drag and drop
    const preventDrag = (e) => {
      e.preventDefault();
      return false;
    };

    element.addEventListener('dragstart', preventDrag);
    element.addEventListener('drop', preventDrag);

    return () => {
      element.removeEventListener('dragstart', preventDrag);
      element.removeEventListener('drop', preventDrag);
    };
  }, [level]);

  // Security level styles
  const securityStyles = {
    low: {
      // Basic protection
    },
    medium: {
      WebkitUserSelect: allowSelection ? 'text' : 'none',
      MozUserSelect: allowSelection ? 'text' : 'none',
      msUserSelect: allowSelection ? 'text' : 'none',
      userSelect: allowSelection ? 'text' : 'none',
    },
    high: {
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
      WebkitTouchCallout: 'none',
      pointerEvents: 'auto', // Keep interactions enabled
    }
  };

  const appliedStyles = {
    ...securityStyles[level],
    ...style
  };

  return (
    <div
      ref={contentRef}
      className={`secure-content secure-level-${level} ${className}`}
      style={appliedStyles}
      data-watermark={watermark ? 'true' : 'false'}
    >
      {children}
    </div>
  );
};

SecureContent.propTypes = {
  children: PropTypes.node.isRequired,
  level: PropTypes.oneOf(['low', 'medium', 'high']),
  className: PropTypes.string,
  watermark: PropTypes.bool,
  allowSelection: PropTypes.bool,
  style: PropTypes.object,
};

export default SecureContent;
